<template>
  <div>
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />

    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("msg.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader />

      <main class="flex flex-col space-y-8 px-12 py-10">
        <div class="flex items-start justify-between">
          <div class="flex flex-col space-y-3">
            <div class="flex items-center gap-3">
              <h1
                tabindex="-1"
                class="no-focus-outline scroll-m-20 text-3xl font-bold tracking-tight"
                ref="initialFocus"
              >
                {{ t("pages.admins.heading.title") }}
              </h1>
              <a
                class="ml-2 inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                href="https://docs.knocknoc.io/books/admin-guide/page/admins"
                target="_blank"
                :aria-label="`${t('pages.admins.docs')} (${t('common.opensInNewTab')})`"
              >
                {{ t("common.docs") }}
                <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
              </a>
            </div>

            <div class="max-w-2xl">
              <p class="text-base leading-relaxed text-muted-foreground">
                {{ t("pages.admins.heading.description") }}
              </p>
            </div>
          </div>

          <div class="flex flex-wrap-reverse justify-end gap-2">
            <button
              type="button"
              @click="invalidateSessions()"
              class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm ring-offset-background transition-all duration-200 hover:bg-primary/90 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
            >
              {{ t("pages.admins.heading.actions.invalidate") }}
            </button>
            <button
              type="button"
              @click="
                closeModal();
                openModal();
              "
              class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm ring-offset-background transition-all duration-200 hover:bg-primary/90 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
            >
              {{ t("pages.admins.heading.actions.create") }}
            </button>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead :aria-sort="sortBy === 'Name' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                <SortButton @click="handleSortChange('Name')">
                  {{ t("pages.admins.tables.admins.columns.Name") }}
                </SortButton>
              </TableHead>
              <TableHead :aria-sort="sortBy === 'AuthType' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                <SortButton @click="handleSortChange('AuthType')">
                  {{ t("pages.admins.tables.admins.columns.AuthType") }}
                </SortButton>
              </TableHead>
              <TableHead :aria-sort="sortBy === 'UserName' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                <SortButton @click="handleSortChange('UserName')">
                  {{ t("pages.admins.tables.admins.columns.UserName") }}
                </SortButton>
              </TableHead>
              <TableHead :aria-sort="sortBy === 'LastLogin' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                <SortButton @click="handleSortChange('LastLogin')">
                  {{ t("pages.admins.tables.admins.columns.LastLogin") }}
                </SortButton>
              </TableHead>
              <TableHead :aria-sort="sortBy === 'SessionIps' ? (sortDesc ? 'descending' : 'ascending') : undefined">
                <SortButton @click="handleSortChange('SessionIps')"> Active IPs </SortButton>
              </TableHead>
              <TableHead class="relative">
                <span class="sr-only">{{ t("msg.edit") }}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow v-for="(admin, index) in filterData" :key="admin.ID">
              <TableCell>
                {{ admin.Name }}
              </TableCell>
              <TableCell>
                {{ admin.AuthType }}
              </TableCell>
              <TableCell>
                {{ admin.UserName }}
                <span v-if="!admin.LoginEnabled" class="text-muted-foreground"
                  >({{ t("common.disabled").toUpperCase() }})</span
                >
              </TableCell>
              <TableCell>
                {{ admin.LastLogin ? DateTime.fromISO(admin.LastLogin).toLocaleString(DateTime.DATETIME_MED) : "-" }}
              </TableCell>
              <TableCell>
                <template v-if="admin?.SessionIps?.length">
                  {{ admin?.SessionIps?.reduce((x, acc) => `${acc}${acc?.length > 0 ? ", " : ""}${x}`) }}
                </template>
              </TableCell>
              <TableCell>
                <Menu as="div" class="relative inline-block text-left">
                  <MenuButton
                    ref="menuButtonsRefs"
                    :aria-label="t('common.actions')"
                    class="inline-flex h-10 w-10 items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                    @click="(e) => e.stopPropagation()"
                  >
                    <EllipsisVerticalIcon class="h-4 w-4" />
                  </MenuButton>

                  <Teleport to="body">
                    <MenuItems
                      as="div"
                      class="fixed z-[100] w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-background shadow-lg ring-1 ring-black/5 focus:outline-none"
                      :style="{
                        top: (menuItemPositions[index]?.top || 0) + 40 + 'px',
                        left: (menuItemPositions[index]?.left || 0) - 120 + 'px',
                      }"
                    >
                      <div class="px-1 py-1">
                        <MenuItem>
                          <button
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                            @click="
                              currentAdmin = admin;
                              fetchAdminData(admin.ID);
                              viewModal = true;
                            "
                          >
                            <EyeIcon class="mr-2 h-5 w-5" />
                            {{ t("pages.admins.tables.admins.actions.view") }}
                          </button>
                        </MenuItem>

                        <MenuItem>
                          <button
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                            @click="
                              currentAdmin = admin;
                              openModal();
                            "
                          >
                            <PencilSquareIcon class="mr-2 h-5 w-5" />
                            {{ t("pages.admins.tables.admins.actions.edit") }}
                          </button>
                        </MenuItem>

                        <MenuItem v-if="admin.TOTPEnabled">
                          <button
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                            @click="
                              currentAdmin = admin;
                              resetOtpModal = true;
                            "
                          >
                            <KeyIcon class="mr-2 h-5 w-5" />
                            {{ t("pages.admins.tables.admins.actions.2fa") }}
                          </button>
                        </MenuItem>

                        <MenuItem>
                          <button
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-primary/20"
                            @click="
                              currentAdmin = admin;
                              deleteModal = true;
                            "
                            aria-label="Delete admin"
                          >
                            <TrashIcon class="mr-2 h-5 w-5" />
                            {{ t("pages.admins.tables.admins.actions.delete") }}
                          </button>
                        </MenuItem>
                      </div>
                    </MenuItems>
                  </Teleport>
                </Menu>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </main>
    </div>
  </div>

  <Dialog :show="viewModal" v-if="currentAdmin">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{ t("msg.adminInfo") }}
      </DialogTitle>

      <div class="max-w-2xl overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-700">
          <tbody class="divide-y divide-gray-800 text-sm">
            <tr>
              <td class="font-semibold">{{ t("fields.users.name") }}</td>
              <td>{{ currentAdmin.Name }}</td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("fields.users.authType") }}</td>
              <td>{{ currentAdmin.AuthType }}</td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("fields.users.username") }}</td>
              <td>{{ currentAdmin.UserName }}</td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("fields.users.totpEnabled") }}</td>
              <td>{{ currentAdmin.TOTPEnabled ? t("msg.yes") : t("msg.no") }}</td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("fields.users.createdAt") }}</td>
              <td>
                {{
                  currentAdmin.CreatedAt
                    ? DateTime.fromISO(currentAdmin.CreatedAt).toLocaleString(DateTime.DATETIME_MED)
                    : t("msg.unknown")
                }}
              </td>
            </tr>
            <tr>
              <td class="font-semibold">{{ t("fields.users.lastLogin") }}</td>
              <td>
                {{
                  currentAdmin.LastLogin
                    ? DateTime.fromISO(currentAdmin.LastLogin).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
                    : t("msg.never")
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <DialogTitle>
        {{ t("fields.sessions.sessions") }}
      </DialogTitle>

      <div class="max-w-2xl overflow-x-auto">
        <p v-if="currentAdminSessions.length <= 0">{{ t("msg.noActiveSessions") }}</p>
        <table v-else class="min-w-full divide-y divide-gray-700">
          <thead>
            <tr class="text-left font-semibold">
              <th scope="col">{{ t("fields.sessions.loginTime") }}</th>
              <th scope="col">{{ t("fields.sessions.ip") }}</th>
              <th scope="col" class="relative">
                <span class="sr-only">{{ t("msg.edit") }}</span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800 text-sm">
            <tr v-for="session in currentAdminSessions">
              <td>{{ session.Created }}</td>
              <td>{{ session.IPAddress }}</td>
              <td class="relative py-4 pl-3 pr-4 text-right font-medium">
                <a href="#" class="text-[#e05454]" @click="deleteSession(session.ID)">
                  {{ t("msg.delete") }}
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <button
        type="button"
        @click="closeModal"
        ref="cancelButtonRef"
        class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
      >
        {{ t("common.close") }}
      </button>
    </DialogPanel>
  </Dialog>

  <Dialog :show="resetOtpModal" v-if="currentAdmin">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{ t("msg.reset2fa") }}
      </DialogTitle>

      <DialogDescription>
        {{ t("msg.reset2FAConfirmation") }}
      </DialogDescription>

      <div class="flex space-x-3">
        <button
          type="button"
          class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
          @click="closeModal"
          ref="cancelButtonRef"
        >
          {{ t("msg.cancel") }}
        </button>

        <button
          type="button"
          class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#e05454] sm:col-start-2"
          @click="resetOtp()"
        >
          {{ t("msg.reset") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <Dialog :show="deleteModal" v-if="currentAdmin">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{ t("msg.deleteAdmin") }}
      </DialogTitle>

      <DialogDescription>
        {{ t("msg.confirmDelete", { name: currentAdmin.Name }) }}
      </DialogDescription>

      <div class="flex space-x-3">
        <button
          type="button"
          class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
          @click="closeModal"
          ref="cancelButtonRef"
        >
          {{ t("msg.cancel") }}
        </button>

        <button
          type="button"
          class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#e05454] sm:col-start-2"
          @click="deleteAdmin"
        >
          {{ t("msg.delete") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <Dialog :show="createModal">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{ currentAdmin == null ? t("msg.createAdmin") : t("msg.updateAdmin") }}
      </DialogTitle>

      <FormKit
        v-model="adminForm"
        type="form"
        :actions="false"
        @submit="saveAdmin"
        @submit-invalid="globals.focusFirstError"
      >
        <div class="grid grid-cols-2 gap-4">
          <FormKit
            name="Name"
            :classes="{ outer: '!max-w-full' }"
            :label="t('fields.users.fullName')"
            placeholder="John Doe"
            type="text"
            validation="required"
            :autocomplete="autoComplete"
          />

          <FormKit
            name="UserName"
            :disabled="currentAdmin !== null"
            :classes="{ outer: '!max-w-full' }"
            :label="t('fields.users.username')"
            placeholder="john.doe"
            type="text"
            validation="required"
            @focus="hasUserNameBeenModified = true"
            :autocomplete="autoComplete"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <FormKit name="TOTPEnabled" :label="t('fields.users.totpEnabled')" type="checkbox" :value="true" />
          <FormKit name="LoginEnabled" :label="t('fields.users.loginEnabled')" type="checkbox" :value="true" />
        </div>

        <FormKit
          name="AuthType"
          :classes="{ outer: '!max-w-full' }"
          :label="t('fields.users.authType')"
          :options="
            ['local', 'SAML', 'LDAP'].map((option) => ({
              value: option,
              label: t(`pages.users.forms.create.fields.AuthType.options.${option}`),
            }))
          "
          type="select"
          validation="required"
        />

        <template v-if="adminForm.AuthType === 'local'">
          <div class="mb-4 flex flex-col">
            <div class="grid grid-cols-2 gap-4">
              <FormKit
                :classes="{
                  outer: 'max-w-full mb-2',
                }"
                name="PlainPassword"
                :label="t('pages.users.forms.create.fields.Password.label')"
                type="password"
                :validation="currentAdmin ? [['matches', /^$|^.{8,64}$/]] : 'required|length:8,64'"
                :validation-messages="{ matches: 'Must be between 8 and 64 characters' }"
              />

              <FormKit
                :classes="{
                  outer: 'max-w-full mb-2',
                }"
                name="ConfirmPassword"
                :label="t('pages.users.forms.create.fields.ConfirmPassword.label')"
                type="password"
                :validation="currentAdmin ? 'passwordsMustMatch' : 'required|passwordsMustMatch'"
                :validation-rules="{ passwordsMustMatch }"
                :validation-messages="{
                  passwordsMustMatch: t('err.passwordsDontMatch'),
                }"
              />
            </div>

            <PasswordMeter :value="adminForm.PlainPassword" />
          </div>
        </template>

        <FormKit v-if="adminForm.AuthType === 'LDAP'" name="ThirdPartyAuth" type="group">
          <FormKit
            name="DN"
            :classes="{ outer: '!max-w-full' }"
            :label="t('fields.users.dn')"
            placeholder="e.g. uid=john.doe,ou=People,dc=example,dc=com"
            type="text"
            :autocomplete="autoComplete"
          />
        </FormKit>

        <div class="grid grid-cols-2 gap-4">
          <button
            type="button"
            @click="closeModal"
            class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0"
          >
            {{ t("msg.cancel") }}
          </button>

          <FormKit
            type="submit"
            :classes="{
              outer: '$reset',
              input:
                '$reset inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2',
            }"
            :disabled="isSubmitting || disabled"
          >
            <font-awesome-icon v-if="isSubmitting" icon="fa-sharp-duotone fa-solid fa-spinner-third" spin />
            {{ currentAdmin == null ? t("msg.create") : t("msg.update") }}
          </FormKit>
        </div>
      </FormKit>
    </DialogPanel>
  </Dialog>

  <ErrorNotification :error="errorMessage" :title="t('msg.error')" @update:error="(input) => (errorMessage = input)" />

  <InfoNotification :text="infoMessage" @update:info="(input) => (infoMessage = input)" />
</template>

<script setup>
import { DialogDescription, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import {
  Bars3Icon,
  EllipsisVerticalIcon,
  EyeIcon,
  KeyIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/vue/24/solid";
import { ArrowTopRightOnSquareIcon } from "@heroicons/vue/24/outline";
import cloneDeep from "lodash/cloneDeep";
import { DateTime } from "luxon";
import { computed, nextTick, onMounted, onUnmounted, ref, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import globals from "../../globals";
import userService from "../../services/user.service.ts";

import SideBar from "../../components/SideBar.vue";
import SearchBar from "../../components/SearchBar.vue";
import ErrorNotification from "../../components/ErrorNotification.vue";
import InfoNotification from "../../components/InfoNotification.vue";
import Dialog from "../../components/ui/dialog/Dialog.vue";
import DialogOverlay from "../../components/ui/dialog/DialogOverlay.vue";
import DialogPanel from "../../components/ui/dialog/DialogPanel.vue";
import DialogTitle from "../../components/ui/dialog/DialogTitle.vue";
import Table from "../../components/ui/table/Table.vue";
import TableHeader from "../../components/ui/table/TableHeader.vue";
import TableBody from "../../components/ui/table/TableBody.vue";
import TableRow from "../../components/ui/table/TableRow.vue";
import TableHead from "../../components/ui/table/TableHead.vue";
import TableCell from "../../components/ui/table/TableCell.vue";
import SortButton from "../../components/ui/table/SortButton.vue";
import PasswordMeter from "../../components/PasswordMeter.vue";
import AdminHeader from "../../components/AdminHeader.vue";
import { useUserStore } from "../../pinia/user.ts";

const { t } = useI18n();

const userStore = useUserStore();

const initialFocus = ref(null);

const errorMessage = ref("");
const infoMessage = ref("");

const autoComplete = import.meta.env?.VITE_AUTOCOMPLETE || "off";

const search = ref("");
const sidebarOpen = ref(false);
const createModal = ref(false);
const deleteModal = ref(false);
const resetOtpModal = ref(false);
const viewModal = ref(false);

const menuButtonsRefs = ref([]);
const menuItemPositions = ref([]);
function updateMenuItemPositions() {
  menuItemPositions.value = menuButtonsRefs.value.map((ref) => ref.el.getBoundingClientRect());
}

const currentAdmin = ref(null);
const currentAdminSessions = ref([]);

const admins = ref([]);

const adminForm = ref({});
const hasUserNameBeenModified = ref(false);

watchEffect(() => {
  if (!currentAdmin.value && !hasUserNameBeenModified.value && adminForm.value?.Name) {
    adminForm.value.UserName = adminForm.value.Name.toLowerCase().split(" ").join(".");
  }
});

function passwordsMustMatch(node) {
  return node.value === node.at("PlainPassword").value;
}

const closeModal = () => {
  createModal.value = false;
  deleteModal.value = false;
  resetOtpModal.value = false;
  viewModal.value = false;
  adminForm.value = {};
  currentAdmin.value = null;
};

const openModal = () => {
  if (currentAdmin.value !== null) {
    adminForm.value = cloneDeep(currentAdmin.value);
  }
  createModal.value = true;
};

const deleteAdmin = () => {
  if (currentAdmin.value !== null) {
    userService.deleteAdmin(currentAdmin.value.ID).then(
      () => {
        infoMessage.value = t("msg.adminDeleted");
        fetchData();
      },
      (error) => {
        errorMessage.value = globals.renderError(t, error);
        fetchData();
      },
    );
  }
  deleteModal.value = false;
  currentAdmin.value = null;
};

const saveAdmin = (fields) => {
  const data = {
    ID: currentAdmin.value?.ID,
    ...fields,
  };

  if (currentAdmin.value !== null) {
    userService.updateAdmin(currentAdmin.value.ID, data).then(
      (response) => {
        infoMessage.value = t("msg.adminUpdated");
        fetchData();
      },
      (error) => {
        fetchData();
        errorMessage.value = globals.renderError(t, error);
      },
    );
  } else {
    userService.createAdmin(JSON.stringify(data)).then(
      (response) => {
        infoMessage.value = t("msg.adminCreated");
        fetchData();
      },
      (error) => {
        fetchData();
        errorMessage.value = globals.renderError(t, error);
      },
    );
  }

  closeModal();
};

const sortBy = ref("Name");
const sortDesc = ref(false);

function handleSortChange(column) {
  if (sortBy.value === column) {
    sortDesc.value = !sortDesc.value;
    return;
  }

  sortBy.value = column;
  sortDesc.value = false;
}

const filterData = computed(() => {
  if (!admins.value) return [];
  const searchTerm = search.value?.toString().toLocaleLowerCase() ?? "";

  const filtered = admins.value.filter((item) => {
    if (!searchTerm) return true;
    return Object.values(item).some((val) => val?.toString().toLocaleLowerCase().includes(searchTerm) ?? false);
  });

  const sorted = [...filtered].sort(globals.smartSortFunc(globals.sortObjBy(sortBy.value)));
  return sortDesc.value ? sorted.reverse() : sorted;
});

const invalidateSessions = () => {
  userService.invalidateAllAdminSessions();
};

const deleteSession = (id) => {
  userService.deleteAdminSession(id).then(() => fetchAdminData(currentAdmin.value.ID));
};

const resetOtp = () => {
  if (currentAdmin.value !== null) {
    userService.resetAdminOtp(currentAdmin.value.ID).then(
      () => {
        infoMessage.value = t("msg.2faReset");
        fetchData();
      },
      (error) => {
        errorMessage.value = globals.renderError(t, error);
      },
    );
  }
  resetOtpModal.value = false;
  currentAdmin.value = null;
};

const fetchAdminData = (id) => {
  userService.getAdminSessions(id).then(
    (response) => {
      currentAdminSessions.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
};

const fetchData = () => {
  userStore.fetchAdmins({ force: true }).then(
    (response) => {
      admins.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
};

watch(initialFocus, async (el) => {
  if (el) {
    await nextTick();
    el.focus();
  }
});

onMounted(() => {
  fetchData();

  window.addEventListener("scroll", updateMenuItemPositions);
  window.addEventListener("resize", updateMenuItemPositions);
});

onUnmounted(() => {
  window.removeEventListener("scroll", updateMenuItemPositions);
  window.removeEventListener("resize", updateMenuItemPositions);
});

watch(() => menuButtonsRefs.value.length, updateMenuItemPositions);
</script>
